// "/": send people to the server they were last in (else their first), or let the owner create one. "/join": paste an
// invite link or code (the server menu's Join a server, #140), which opens the invite as its link would.
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import type { ServerSummary } from "@bloom/shared";
import { api } from "../api.ts";
import { useSession } from "../session.tsx";
import { lastServer } from "../lastVisit.ts";
import { AuthCard, Field } from "../components/ui.tsx";

export function Home({ create = false }: { create?: boolean }) {
  const { me, refresh } = useSession();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  if (me === undefined) return null;
  if (me === null) return <Navigate to="/login" replace />;
  if (me.servers[0] && !create) {
    // The server I was last in on this device, if I'm still in it.
    const last = lastServer();
    const to = me.servers.find((s) => s.id === last) ?? me.servers[0];
    return <Navigate to={`/s/${to.id}`} replace />;
  }
  return (
    <AuthCard>
      <h1>Hi {me.name}</h1>
      <p style={{ margin: 0 }}>{create ? "Start a new server for another group." : "You're not in a server yet. Open an invite link from a friend, or start your own."}</p>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            const s = await api<ServerSummary>("/servers", { body: { name } });
            await refresh();
            navigate(`/s/${s.id}`, { replace: true });
          } catch (err) {
            setError((err as Error).message);
          }
        }}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        <Field id="server-name" label="Server name" value={name} onChange={setName} autoFocus />
        {error && <p className="error" role="alert">{error}</p>}
        <button className="btn btn-primary">Create server</button>
      </form>
    </AuthCard>
  );
}

/** The code from a pasted invite link ("https://…/invite/abc"), or the text itself if it's just the code. */
export function inviteCode(text: string): string {
  const t = text.trim();
  return t.match(/\/invite\/([^/?#\s]+)/)?.[1] ?? t.replace(/^\/+|\/+$/g, "");
}

export function JoinServer() {
  const { me } = useSession();
  const navigate = useNavigate();
  const [link, setLink] = useState("");
  if (me === undefined) return null;
  if (me === null) return <Navigate to="/login?next=%2Fjoin" replace />;
  return (
    <AuthCard>
      <h1>Join a server</h1>
      <p style={{ margin: 0 }}>Paste the invite link a friend sent you.</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const code = inviteCode(link);
          if (code) navigate(`/invite/${encodeURIComponent(code)}`);
        }}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        <Field id="invite-link" label="Invite link or code" value={link} onChange={setLink} autoFocus />
        <button className="btn btn-primary" disabled={!inviteCode(link)}>Continue</button>
      </form>
    </AuthCard>
  );
}
